import React from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import * as editableDocument from 'utils/model/editableDocument';
import {
  isEmail,
  isDerivedInRange,
  validateCompanyOrPersonUniqueId,
  phone,
  isMandatory,
  hasLengthLessThan,
  validateCompanyUniqueId,
  validatePersonUniqueId
} from 'utils/simple-validations';
import { extractAgeFromCnp } from 'utils/functions';
import { minimumAge, maximumAge } from 'constants/numericLimits';
import OfferAssetClientCard from './OfferAssetClientCard';
import FacilityFrameOfferAssetClientCard from './FacilityFrameOfferAssetClientCard';
import {
  loadOfferAssetClient,
  loadAssetClientByUniqueId,
  updateOfferAssetClient,
  toggleAssetClientCard,
  cancelAssetClientChanges,
  invalidateOfferAssetClient
} from '../actionCreators';
import { loadLegalPartners, loadPFPartners, loadCitizenships } from 'features/offer/edit/client/form/actionCreators';
import { loadLegalForms, loadCountries, loadCounties, loadCities } from 'actions/actionCreators/dictionariesActions';
import { onPrintGDPR, loadRelations, loadGDPR, loadPartnerTypes } from 'features/offer/edit/client/form/actionCreators';
import curry from 'lodash.curry';
import flow from 'lodash.flow';
import {
  getFullOfferAssetClient,
  getOfferAssetClientData,
  getPartnerByUniqueId,
  getFilteredPartnerTypes,
  getClientSummary
} from '../selectors';
import { withParams } from 'providers/ParamsConsumer';
import { withApplicationFeatures } from 'providers/ApplicationFeaturesConsumer';
import { when } from 'utils/fn-combinators';
import { emptyString } from 'constants/emptyConst';

const getValidationRules = (
  partnerTypes,
  partnerTypeId,
  minAge,
  maxAge,
  uniqueIdErrorMessage,
  applyUniqueIdRules,
  countryCode,
  partnerNameMandatory
) => {
  const partnerType = partnerTypes.find(pt => pt.partnerTypeId === partnerTypeId);
  if (!!partnerTypeId && partnerType && partnerType.typeCheck.isPJ) {
    return {
      generalData: {
        uniqueId: [when(applyUniqueIdRules, validateCompanyUniqueId(countryCode))],
        partnerName: [hasLengthLessThan(200)]
      },
      contactData: {
        email: [isEmail()],
        phone: [phone(countryCode)]
      }
    };
  }

  if (!!partnerTypeId && partnerType && partnerType.typeCheck.isPFA) {
    return {
      generalData: {
        uniqueId: [when(applyUniqueIdRules, validateCompanyOrPersonUniqueId(countryCode))],
        partnerName: [hasLengthLessThan(200)]
      },
      contactData: {
        email: [isEmail()],
        phone: [phone(countryCode)]
      }
    };
  }

  return {
    generalData: {
      uniqueId: [
        when(applyUniqueIdRules, validatePersonUniqueId(countryCode), {
          ...isDerivedInRange(extractAgeFromCnp, minAge, maxAge),
          msg: uniqueIdErrorMessage
        })
      ],
      firstName: [when(partnerNameMandatory, isMandatory()), hasLengthLessThan(150)],
      lastName: [when(partnerNameMandatory, isMandatory()), hasLengthLessThan(150)]
    },
    contactData: {
      email: [isEmail()],
      phone: [phone(countryCode)]
    }
  };
};

const setDocumentDefaults = (document, defaultRelationId) => {
  return {
    ...document,
    generalData: {
      ...document.generalData,
      relationId: document?.generalData?.relationId || defaultRelationId
    }
  };
};

class OfferAssetClientContainer extends React.PureComponent {
  state = {
    client: null,
    partnerFromModal: false,
    prevProps: {
      client: null,
      partnerByUniqueId: null
    }
  };

  applyUniqueIdRules = (uniqueId, isExternal = false) => {
    const specialIds = this.props.params.specialPartnerUniqueIds;
    if (isExternal) return false;
    if (uniqueId && uniqueId.toUpperCase().startsWith(this.props.params.keysFinNextIdPrefix)) return false;
    if (uniqueId !== null && specialIds && specialIds.includes(uniqueId)) return false;

    return true;
  };

  static getDerivedStateFromProps(props, state) {
    const prevProps = state.prevProps;
    if (
      props.isLoadedByUniqueId &&
      props.partnerByUniqueId !== prevProps.partnerByUniqueId &&
      props.partnerByUniqueId.generalData.uniqueId !== undefined
    ) {
      let client = editableDocument.create(setDocumentDefaults(props.partnerByUniqueId, props.config.defaultRelationId));
      if (state.client && state.client.document) {
        if (
          state.client.document.partnerType &&
          state.client.document.partnerType?.typeCheck?.isPF &&
          state.client.document.agreesWithPersonalDataUse
        ) {
          client = editableDocument.update('agreesWithPersonalDataUse', true, null, client);
        } else {
          client = editableDocument.update('agreesWithPersonalDataUse', false, null, client);
        }

        client = editableDocument.update('partnerType', state.client.document.partnerType, null, client);
      }

      return {
        prevProps: {
          client: props.client,
          partnerByUniqueId: props.partnerByUniqueId
        },
        client
      };
    }

    const emails = props.client.dealerContactEmails?.filter(x => x.dealerContactModeId > 0);
    const phones = props.client.dealerContactPhones?.filter(x => x.dealerContactModeId > 0);
    const contacts = props.client.dealerContactInfos?.filter(x => x.dealerContactInfoId > 0);

    if (
      !prevProps.client ||
      (props.client?.partnerId && prevProps.client?.partnerId !== 0 && props.client.partnerId !== prevProps.client.partnerId) ||
      (prevProps.client?.partnerType &&
        props.client?.partnerType &&
        props.client?.partnerType?.partnerTypeId !== prevProps.client?.partnerType?.partnerTypeId) ||
      (emails?.length > 0 &&
        emails?.length !== state.client.document.dealerContactEmails?.filter(x => x.dealerContactModeId > 0)?.length) ||
      (phones?.length > 0 &&
        phones?.length !== state.client.document.dealerContactPhones?.filter(x => x.dealerContactModeId > 0)?.length) ||
      (contacts?.length > 0 &&
        contacts?.length !== state.client.document.dealerContactInfos?.filter(x => x.dealerContactInfoId > 0)?.length)
    ) {
      let client = editableDocument.create(setDocumentDefaults(props.client, props.config.defaultRelationId));
      if (state.client && state.client.document && state.client.document.agreesWithPersonalDataUse) {
        client = editableDocument.update('agreesWithPersonalDataUse', true, null, client);
      }

      if (
        state.client &&
        state.client.dirtyInfo &&
        (state.client?.partnerType || prevProps.client?.partnerType?.partnerTypeId !== props.client?.partnerType?.partnerTypeId) &&
        prevProps.client?.partnerType !== state.client?.partnerType
      )
        client = editableDocument.update('dirtyInfo', state.client.dirtyInfo, null, client);

      return {
        prevProps: {
          client: props.client,
          partnerByUniqueId: props.partnerByUniqueId
        },
        client
      };
    }
    return null;
  }

  componentDidMount() {
    const { actions, documentId, siteId, documentTypeId } = this.props;
    actions.loadOfferAssetClient(documentId, siteId, documentTypeId);
    actions.loadRelations();
    actions.loadPartnerTypes();
    actions.loadLegalForms();
    actions.loadCitizenships();
  }

  componentDidUpdate(prevProps) {
    const { client, actions, documentId, siteId } = this.props;
    if (client.isLoaded !== prevProps.client.isLoaded && !client.isLoaded) {
      actions.loadOfferClient(documentId, siteId);
    }
  }

  componentWillUnmount() {
    this.props.actions.cancelAssetClientChanges(this.props.documentId, this.props.siteId);
  }

  handleUniqueIdBlur = uniqueId => {
    const { partnerTypes } = this.props;
    const { partnerType } = this.state.client.document;
    if (uniqueId && this.state.client.validationInfo.generalData && !this.state.client.validationInfo.generalData.uniqueId) {
      if (
        this.props.loadedWithErrors ||
        !this.props.isLoadedByUniqueId ||
        this.props.isLoadedByUniqueId.replace(/\s/g, '') !== uniqueId.replace(/\s/g, '')
      ) {
        const partnerTypeId = partnerType?.partnerTypeId;
        if (uniqueId && !!partnerTypeId) {
          const partnerType = partnerTypes.find(pt => pt.partnerTypeId === partnerTypeId);
          this.props.actions.loadAssetClientByUniqueId(
            this.props.documentId,
            this.props.siteId,
            uniqueId,
            partnerType,
            this.state.client.document?.generalData.isExternal
          );
          this.invalidateOfferAssetClient(partnerType);
        }
      } else this.resetPartnerByUniqueId();
    }
  };

  handlePropertyChange = curry((prop, value) => {
    const { partnerTypes } = this.props;
    const { partnerType } = this.state.client.document;
    const minAge = this.props.client.minimumAge || minimumAge;
    const maxAge = this.props.client.maximumAge || maximumAge;
    const uniqueIdErrorMessage = this.props.t('Validations.Client.Age', {
      data: { minimumAge: minAge, maximumAge: maxAge }
    });
    const uniqueId = prop.includes('uniqueId') ? value : this.state.client?.document?.generalData?.uniqueId;
    const partnerNameMandatory = !this.state.client.validationInfo.isValid && prop.includes('uniqueId') === false;

    const updateClient = (prop, value, client) => {
      return flow(
        editableDocument.update(prop, value, null),
        editableDocument.validate(
          getValidationRules(
            partnerTypes,
            prop === 'partnerType' ? value?.partnerTypeId : partnerType?.partnerTypeId,
            minAge,
            maxAge,
            uniqueIdErrorMessage,
            this.applyUniqueIdRules(uniqueId, isExternal),
            this.props.params.countryCode,
            partnerNameMandatory
          ),
          false
        )
      )(client);
    };

    if (prop === 'partnerType' && (typeof partnerType == 'undefined' || partnerType?.partnerTypeId !== value.partnerTypeId))
      this.invalidateOfferAssetClient(value);

    const isExternal = prop.includes('generalData.isExternal') ? value : !!this.state.client?.document?.generalData?.isExternal;

    let newClient = this.state.client;
    if (prop === 'contactData.dealerContactModeEmailId') {
      const dealerContactModeEmail = this.state.client.document.dealerContactEmails?.find(x => x.dealerContactModeId === value);
      if (dealerContactModeEmail) newClient = updateClient('contactData.email', dealerContactModeEmail.email, newClient);
      else {
        const newIdx = (this.state.client.document.dealerContactEmails?.length || 0) * -1;
        newClient = updateClient('contactData.email', value, newClient);
        newClient = updateClient(
          'dealerContactEmails',
          [
            ...(newClient.document.dealerContactEmails?.filter(x => x.dealerContactModeId > 0) || []),
            {
              dealerContactModeId: newIdx,
              email: value,
              isActive: true
            }
          ],
          newClient
        );
        value = newIdx;
      }
    }

    if (prop === 'contactData.dealerContactModePhoneId') {
      const dealerContactModePhone = this.state.client.document.dealerContactPhones?.find(x => x.dealerContactModeId === value);
      if (dealerContactModePhone) newClient = updateClient('contactData.phone', dealerContactModePhone.phone, newClient);
      else {
        const newIdx = (this.state.client.document.dealerContactPhones?.length || 0) * -1;
        newClient = updateClient('contactData.phone', value, newClient);
        newClient = updateClient(
          'dealerContactPhones',
          [
            ...(newClient.document.dealerContactPhones?.filter(x => x.dealerContactModeId > 0) || []),
            {
              dealerContactModeId: newIdx,
              phone: value,
              isActive: true
            }
          ],
          newClient
        );
        value = newIdx;
      }
    }

    if (prop === 'contactData.dealerContactInfoId') {
      const dealerContactInfoData = this.state.client.document.dealerContactInfos?.find(x => x.dealerContactInfoId === value);
      if (dealerContactInfoData) {
        newClient = updateClient('contactData.phone', dealerContactInfoData.phone, newClient);
        newClient = updateClient('contactData.email', dealerContactInfoData.email, newClient);
        newClient = updateClient('contactData.personName', dealerContactInfoData.personName, newClient);
      } else {
        const newIdx = (this.state.client.document.dealerContactInfos?.length || 0) * -1;
        newClient = updateClient('contactData.phone', emptyString, newClient);
        newClient = updateClient('contactData.email', emptyString, newClient);
        newClient = updateClient('contactData.personName', value, newClient);
        newClient = updateClient(
          'dealerContactInfos',
          [
            ...(newClient.document.dealerContactInfos?.filter(x => x.dealerContactInfoId > 0) || []),
            {
              dealerContactInfoId: newIdx,
              personName: value,
              email: null,
              phone: null,
              isActive: true
            }
          ],
          newClient
        );
        value = newIdx;
      }
    }

    newClient = updateClient(prop, value, newClient);
    this.setState(
      () => ({ client: newClient }),
      () => {
        if (this.state.partnerFromModal && prop === 'generalData.uniqueId') {
          this.handleUniqueIdBlur(value);
          this.setIsPartnerFromModal(false);
        }
      }
    );
  });

  handleSave = () => {
    const { partnerType, generalData } = this.state.client.document;
    const { partnerTypes } = this.props;
    const minAge = this.props.client.minimumAge || minimumAge;
    const maxAge = this.props.client.maximumAge || maximumAge;
    const uniqueIdErrorMessage = this.props.t('Validations.Client.Age', {
      data: { minimumAge: minAge, maximumAge: maxAge }
    });
    const partnerNameMandatory = this.state.client.document.agreesWithPersonalDataUse && generalData.uniqueId;

    this.setState(
      prevState => {
        const client = editableDocument.validate(
          getValidationRules(
            partnerTypes,
            partnerType?.partnerTypeId,
            minAge,
            maxAge,
            uniqueIdErrorMessage,
            this.applyUniqueIdRules(generalData.uniqueId, generalData.isExternal),
            this.props.params.countryCode,
            partnerNameMandatory
          ),
          false,
          prevState.client
        );
        client.document.isFacilityFrameOffer = this.props.isFacilityFrame;
        if (!client.validationInfo.isValid) {
          return { client };
        }
      },
      () => {
        const { documentId, siteId } = this.props;
        if (this.state.client.validationInfo.isValid) {
          const contactData = { ...this.state.client.document.contactData };
          const client = {
            ...this.state.client.document,
            contactData: {
              ...contactData,
              dealerContactInfoId: contactData.dealerContactInfoId > 0 ? contactData.dealerContactInfoId : null,
              dealerContactModeEmailId: contactData.dealerContactModeEmailId > 0 ? contactData.dealerContactModeEmailId : null,
              dealerContactModePhoneId: contactData.dealerContactModePhoneId > 0 ? contactData.dealerContactModePhoneId : null
            }
          };
          this.props.actions.updateOfferAssetClient(documentId, siteId, client);
          this.setState(prevState => ({
            client: flow(
              editableDocument.resetDirtyInfo,
              editableDocument.resetValidationInfo
            )({
              ...prevState.client,
              document: {
                ...prevState.client.document,
                generalData: {
                  ...prevState.client.document.generalData,
                  uniqueId: prevState.client.document.generalData.uniqueId?.trim(),
                  firstName: prevState.client.document.generalData.firstName?.trim(),
                  lastName: prevState.client.document.generalData.lastName?.trim(),
                  partnerName: prevState.client.document.generalData.partnerName?.trim()
                }
              }
            })
          }));
        }
      }
    );
  };

  onGDPRPrint = async () => {
    const { documentId, siteId, client } = this.props;
    this.props.actions.onPrintGDPR(documentId, siteId, client.partnerId);
  };
  invalidateOfferAssetClient = value => {
    this.props.actions.invalidateOfferAssetClient(value, this.props.client.partnerId);
  };

  setIsPartnerFromModal = value => {
    this.setState({
      partnerFromModal: value
    });
  };

  resetPartnerByUniqueId = () => {
    this.setState(prevState => ({
      ...prevState,
      prevProps: {
        partnerByUniqueId: null
      }
    }));
  };

  resetGDPR = () => {
    this.setState(prevState => ({
      client: flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo)(prevState.client)
    }));
    this.setState(prevState => ({
      ...prevState,
      client: {
        ...prevState.client,
        document: {
          ...prevState.client.document,
          generalData: {},
          contactData: {},
          dealerContactInfos: [],
          dealerContactEmails: [],
          dealerContactPhones: []
        }
      }
    }));
  };

  render() {
    const {
      saving,
      expanded,
      relations,
      gdpr,
      printingGdpr,
      config,
      actions,
      partnerTypes,
      isFacilityFrame,
      legalForms,
      citizenships,
      processing,
      isClientLoaded,
      applicationFeatures,
      clientDataSummary
    } = this.props;
    const { document, validationInfo, dirtyInfo } = this.state.client;
    const partnerType = partnerTypes.find(pt => pt.partnerTypeId === document?.partnerType?.partnerTypeId);
    const { offerForCompanies } = applicationFeatures;
    return isFacilityFrame ? (
      <FacilityFrameOfferAssetClientCard
        client={document}
        clientDataSummary={clientDataSummary}
        isClientLoaded={isClientLoaded}
        validationInfo={validationInfo}
        isDirty={dirtyInfo.isDirty}
        onPropertyChange={this.handlePropertyChange}
        defaultPartnerType={config.defaultPartnerType}
        onUniqueIdBlur={this.handleUniqueIdBlur}
        onSave={this.handleSave}
        saving={saving}
        expanded={expanded}
        onCardToggle={actions.toggleAssetClientCard}
        setIsPartnerFromModal={this.setIsPartnerFromModal}
        partnerTypes={partnerTypes}
        loadLegalPartners={actions.loadLegalPartners}
        partnerType={partnerType}
        legalForms={legalForms}
        processing={processing}
        offerForCompanies={offerForCompanies}
      />
    ) : (
      <OfferAssetClientCard
        client={document}
        clientDataSummary={clientDataSummary}
        document={{ documentId: this.props.documentId, siteId: this.props.siteId }}
        isClientLoaded={isClientLoaded}
        validationInfo={validationInfo}
        isDirty={dirtyInfo.isDirty}
        onPropertyChange={this.handlePropertyChange}
        onUniqueIdBlur={this.handleUniqueIdBlur}
        relations={relations}
        gdpr={gdpr}
        defaultPartnerType={config.defaultPartnerType}
        defaultRelationId={config.defaultRelationId}
        onSave={this.handleSave}
        loadGDPR={actions.loadGDPR}
        onGDPRPrint={this.onGDPRPrint}
        printingGdpr={printingGdpr}
        saving={saving}
        loadLegalPartners={actions.loadLegalPartners}
        legalForms={legalForms}
        setIsPartnerFromModal={this.setIsPartnerFromModal}
        expanded={expanded}
        pfPartners={this.props.actions.loadPFPartners}
        onCardToggle={actions.toggleAssetClientCard}
        partnerTypes={partnerTypes}
        loadCountries={this.props.actions.loadCountries}
        loadCounties={this.props.actions.loadCounties}
        loadCities={this.props.actions.loadCities}
        citizenships={citizenships}
        processing={processing}
        resetGDPR={this.resetGDPR}
        dirtyInfo={dirtyInfo}
        offerForCompanies={offerForCompanies}
      />
    );
  }
}

OfferAssetClientContainer.propTypes = {
  t: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  client: PropTypes.object.isRequired,
  clientDataSummary: PropTypes.object.isRequired,
  partnerByUniqueId: PropTypes.object.isRequired,
  isLoadedByUniqueId: PropTypes.string,
  saving: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  printingGdpr: PropTypes.bool.isRequired,
  relations: PropTypes.array.isRequired,
  gdpr: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  documentTypeId: PropTypes.number.isRequired,
  isFacilityFrame: PropTypes.bool,
  loadLegalPartners: PropTypes.func,
  legalForms: PropTypes.array.isRequired,
  loadCountries: PropTypes.func,
  loadCities: PropTypes.func,
  loadCounties: PropTypes.func,
  citizenships: PropTypes.array.isRequired,
  processing: PropTypes.bool,
  isClientLoaded: PropTypes.bool,
  loadedWithErrors: PropTypes.bool,
  resetGDPR: PropTypes.func,
  params: PropTypes.object.isRequired,
  applicationFeatures: PropTypes.object.isRequired
};

function mapStateToProps(state, ownProps) {
  const { isFacilityFrame } = ownProps;
  const client = getFullOfferAssetClient(state);
  const clientDataSummary = getClientSummary(client);
  const assetClientData = getOfferAssetClientData(state);
  const partnerByUniqueId = getPartnerByUniqueId(state);
  const partnerTypes = getFilteredPartnerTypes(state, isFacilityFrame);
  return {
    client,
    clientDataSummary,
    partnerByUniqueId,
    isLoadedByUniqueId: assetClientData.client.loadedByUniqueId || false,
    isClientLoaded: assetClientData.client.isLoaded,
    loadedWithErrors: assetClientData.client.withErrors,
    saving: assetClientData.saving,
    expanded: assetClientData.expanded || false,
    legalForms: state.dictionaries.legalForms.isLoaded ? state.dictionaries.legalForms.items : [],
    citizenships: state.clientData.citizenships,
    printingGdpr: assetClientData.printingGdpr,
    relations: state.clientData.relations,
    processing: state.currentOffer.header.processing,
    gdpr: state.clientData.gdpr,
    config: state.offerConfig,
    partnerTypes
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadOfferAssetClient,
        loadPartnerTypes,
        updateOfferAssetClient,
        invalidateOfferAssetClient,
        toggleAssetClientCard,
        loadRelations,
        loadAssetClientByUniqueId,
        loadGDPR,
        onPrintGDPR,
        cancelAssetClientChanges,
        loadLegalPartners,
        loadLegalForms,
        loadPFPartners,
        loadCountries,
        loadCounties,
        loadCities,
        loadCitizenships
      },
      dispatch
    )
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation()
)(compose(withParams, withApplicationFeatures)(OfferAssetClientContainer));
